/**
 * The role vocabulary and how to read it out of Clerk's `publicMetadata`.
 *
 * Deliberately a leaf module: `lib/auth.ts` also owns the Drizzle visibility
 * filters, which drag the whole schema graph in. `lib/clerk/directory.ts` needs
 * only the role reading, so it imports this instead and stays cheap to load.
 * `lib/auth.ts` re-exports everything here, so existing importers are unaffected.
 */

import {
  AUDITION_FUNNEL_ROLES,
  type AuditionFunnelRole,
} from '../audition/state-machine.js'

/**
 * User roles for access control.
 * - admin: Full access to all problems and admin features
 * - client: Coaching clients, can see public + client_only problems
 * - AuditionFunnelRole (see lib/audition/state-machine.ts, the single
 *   source of the funnel-role vocabulary): audition-wizard funnel stages
 *   (LG-118). These grant no problem/admin access on their own —
 *   canAccessVisibility/canAccessProblem/getStatusFilter treat them like
 *   `null` (their checks compare against 'admin'/'client' literals only),
 *   getVisibilityFilter handles them via isFunnelRole, and isAdmin/isClient
 *   in api/_middleware/auth.ts compare against the 'admin'/'client'
 *   literals only. They exist so getUserRole round-trips the funnel role
 *   Clerk's publicMetadata.role carries.
 * - null: Regular/unauthenticated users, can only see public problems
 */
export type UserRole = 'admin' | 'client' | AuditionFunnelRole | null

const FUNNEL_ROLES: ReadonlySet<unknown> = new Set(AUDITION_FUNNEL_ROLES)

/**
 * Funnel-stage roles are markers only. `lib/clerk/directory.ts` re-exports this
 * as `isClearableFunnelRole` — the metadata-policy name for the same question.
 */
export function isFunnelRole(value: unknown): value is AuditionFunnelRole {
  return FUNNEL_ROLES.has(value)
}

/**
 * Extract user role from Clerk publicMetadata.
 *
 * @example
 * const role = getUserRole(user.publicMetadata)
 * // role: 'admin' | 'client' | 'audition-completed' | 'paid-pending-call' |
 * //       'vibe-call-booked' | 'payment-unlocked' | null
 */
export function getUserRole(
  publicMetadata: Record<string, unknown> | undefined | null,
): UserRole {
  if (!publicMetadata) return null
  const role = publicMetadata.role
  if (role === 'admin') return 'admin'
  if (role === 'client') return 'client'
  if (isFunnelRole(role)) return role
  return null
}
